import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  PERMISSION_GROUPS,
  ROLES,
  ALL_PERMISSIONS,
} from "../utils/permissions";
import {
  getRolePermissions,
  updateRolePermissions,
} from "../services/rolePermissionService";
import {
  createRole,
  updateRole,
  deleteRole,
} from "../services/rolesService";
import { useAuth } from "../context/AuthContext";

const RolePermissionsLayer = () => {
  const { hasPermission, refresh, refreshRoles, user, roles, roleLabel } = useAuth();
  const canEditPerms = hasPermission("roles.manage_permissions");
  const canManageRoles = hasPermission("roles.manage");

  // The server response is a map of roleKey -> permissionList.
  const [rolePerms, setRolePerms] = useState({});
  const [activeRole, setActiveRole] = useState(ROLES.ADMIN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Role CRUD modal state.
  const [roleModal, setRoleModal] = useState(null); // null | { mode: 'create'|'edit', id?, role_key?, label }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await getRolePermissions();
      if (res?.status) {
        const data = res.data || {};
        // Ensure super_admin is always seeded with the full catalog on the client.
        if (!data[ROLES.SUPER_ADMIN]) data[ROLES.SUPER_ADMIN] = ALL_PERMISSIONS;
        setRolePerms(data);
        // If the current active role no longer exists, jump to the first editable role.
        if (!data[activeRole]) {
          const firstEditable = Object.keys(data).find((k) => k !== ROLES.SUPER_ADMIN);
          if (firstEditable) setActiveRole(firstEditable);
        }
      } else {
        toast.error(res?.msg || "Failed to load permissions");
      }
    } catch (err) {
      toast.error("Failed to load permissions");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = useMemo(() => rolePerms[activeRole] || [], [rolePerms, activeRole]);
  const currentSet = useMemo(() => new Set(current), [current]);

  const activeRoleMeta = useMemo(
    () => roles.find((r) => r.role_key === activeRole) || null,
    [roles, activeRole]
  );

  const isSuperAdminRole = activeRole === ROLES.SUPER_ADMIN;
  const readOnly = isSuperAdminRole || !canEditPerms;

  const togglePerm = (key) => {
    if (readOnly) return;
    setRolePerms((prev) => {
      const arr = prev[activeRole] || [];
      return {
        ...prev,
        [activeRole]: arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key],
      };
    });
  };

  const toggleGroup = (groupKeys, on) => {
    if (readOnly) return;
    setRolePerms((prev) => {
      const arr = new Set(prev[activeRole] || []);
      groupKeys.forEach((k) => (on ? arr.add(k) : arr.delete(k)));
      return { ...prev, [activeRole]: Array.from(arr) };
    });
  };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const res = await updateRolePermissions(activeRole, current);
      if (res?.status) {
        toast.success(res.msg || "Saved");
        if (user?.role === activeRole) await refresh();
      } else {
        toast.error(res?.msg || "Save failed");
      }
    } catch (err) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  /* ---------- Role CRUD handlers ---------- */

  const openCreateRole = () => {
    setRoleModal({ mode: "create", role_key: "", label: "" });
  };

  const openEditRole = () => {
    if (!activeRoleMeta) return;
    setRoleModal({
      mode: "edit",
      id: activeRoleMeta.id,
      role_key: activeRoleMeta.role_key,
      label: activeRoleMeta.label,
      is_system: activeRoleMeta.is_system,
    });
  };

  const submitRoleModal = async (e) => {
    e.preventDefault();
    if (!roleModal) return;
    const { mode } = roleModal;
    try {
      let res;
      if (mode === "create") {
        res = await createRole({
          role_key: roleModal.role_key.trim(),
          label: roleModal.label.trim(),
        });
      } else {
        const payload = { id: roleModal.id, label: roleModal.label.trim() };
        if (!roleModal.is_system) payload.role_key = roleModal.role_key.trim();
        res = await updateRole(payload);
      }
      if (res?.status) {
        toast.success(res.msg || "Role saved");
        setRoleModal(null);
        await refreshRoles();
        await fetchAll();
        if (mode === "create") {
          setActiveRole(roleModal.role_key.trim());
        } else if (!roleModal.is_system) {
          setActiveRole(roleModal.role_key.trim());
        }
      } else {
        toast.error(res?.msg || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const handleDeleteRole = async () => {
    if (!activeRoleMeta || activeRoleMeta.is_system) return;
    if (!window.confirm(`Delete role "${activeRoleMeta.label}"? This removes it from every admin that lists it.`)) return;
    try {
      const res = await deleteRole(activeRoleMeta.id);
      if (res?.status) {
        toast.success(res.msg);
        await refreshRoles();
        await fetchAll();
        setActiveRole(ROLES.ADMIN);
      } else {
        toast.error(res?.msg || "Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <h6 className="mb-0 fw-bold">Role Permissions</h6>
          {roles.map((r) => (
            <button
              key={r.role_key}
              type="button"
              onClick={() => setActiveRole(r.role_key)}
              className={`btn btn-sm ${activeRole === r.role_key ? "btn-primary-600" : "btn-outline-primary-600"}`}
            >
              {r.label}
              {r.is_system ? null : (
                <Icon icon="solar:star-bold" className="ms-1" style={{ fontSize: 12 }} />
              )}
            </button>
          ))}
          {canManageRoles && (
            <button
              type="button"
              onClick={openCreateRole}
              className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
              title="Create a new role"
            >
              <Icon icon="solar:add-circle-bold" />
              New Role
            </button>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isSuperAdminRole && (
            <span className="badge bg-neutral-200 text-secondary-light">
              Super Admin has all permissions
            </span>
          )}
          {!isSuperAdminRole && !canEditPerms && (
            <span className="badge bg-warning-50 text-warning-700">Read only</span>
          )}
          {canManageRoles && !isSuperAdminRole && activeRoleMeta && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary-600"
                onClick={openEditRole}
                title={activeRoleMeta.is_system ? "Rename label (system key is fixed)" : "Rename role"}
              >
                <Icon icon="solar:pen-bold" /> Rename
              </button>
              {!activeRoleMeta.is_system && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDeleteRole}
                  title="Delete this role"
                >
                  <Icon icon="solar:trash-bin-minimalistic-bold" />
                </button>
              )}
            </>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary-600 d-inline-flex align-items-center gap-2"
            >
              <Icon icon="solar:diskette-bold" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-40 text-secondary-light">Loading...</div>
        ) : (
          <>
            <div className="mb-16 text-sm text-secondary-light">
              Editing permissions for <strong>{roleLabel(activeRole)}</strong>
              {activeRoleMeta && !activeRoleMeta.is_system && (
                <span className="badge bg-success-50 text-success-700 ms-2">Custom Role</span>
              )}
            </div>
            <div className="row g-3">
              {PERMISSION_GROUPS.map((group) => {
                const groupKeys = group.permissions.map((p) => p.key);
                const allOn = groupKeys.every((k) => currentSet.has(k));
                const someOn = !allOn && groupKeys.some((k) => currentSet.has(k));
                return (
                  <div key={group.key} className="col-12 col-md-6 col-xl-4">
                    <div
                      className="border rounded-3 p-16 h-100"
                      style={{ background: "#fafbfc", borderColor: "#e5e7eb" }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-12">
                        <div className="fw-bold text-dark">{group.label}</div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            disabled={readOnly}
                            checked={allOn}
                            ref={(el) => el && (el.indeterminate = someOn)}
                            onChange={(e) => toggleGroup(groupKeys, e.target.checked)}
                          />
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {group.permissions.map((p) => {
                          const on = currentSet.has(p.key);
                          return (
                            <label
                              key={p.key}
                              className="d-flex align-items-center gap-2 mb-0"
                              style={{ cursor: readOnly ? "default" : "pointer" }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input"
                                disabled={readOnly}
                                checked={on}
                                onChange={() => togglePerm(p.key)}
                              />
                              <span className="text-sm text-secondary-light">
                                {p.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {roleModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: 16,
          }}
          onClick={() => setRoleModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 460,
              boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-20">
              <h6 className="mb-0 fw-bold">
                {roleModal.mode === "create" ? "Create Role" : "Edit Role"}
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setRoleModal(null)}
              >
                <Icon icon="radix-icons:cross-2" />
              </button>
            </div>
            <form onSubmit={submitRoleModal}>
              <div className="mb-3">
                <label className="form-label">Display Name *</label>
                <input
                  className="form-control"
                  required
                  value={roleModal.label}
                  onChange={(e) => setRoleModal({ ...roleModal, label: e.target.value })}
                  placeholder="e.g. Support Manager"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Role Key * {roleModal.is_system && <span className="badge bg-neutral-200 text-secondary-light ms-1">system</span>}
                </label>
                <input
                  className="form-control"
                  required
                  value={roleModal.role_key}
                  onChange={(e) =>
                    setRoleModal({ ...roleModal, role_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                  }
                  placeholder="support_manager"
                  disabled={roleModal.is_system}
                />
                <div className="text-xs text-secondary-light mt-1">
                  Lowercase letters, digits, underscores. Starts with a letter. Cannot be renamed for system roles.
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setRoleModal(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary-600">
                  {roleModal.mode === "create" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionsLayer;
